import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Plane, Edit, ArrowLeft, Save, Building2, Plus, Trash2, Users, Calendar, Wallet, Search, CheckCircle2, UserCheck, Paperclip, UploadCloud, RotateCcw, Send, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import SearchableProjectSelect from '../../components/SearchableProjectSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BOOKING_PREFERENCE_OPTIONS = {
  flight: [
    { value: 'economy', label: 'Economy Class' }
  ],
  train: [
    { value: '3ac', label: '3 AC' },
    { value: 'sleeper', label: 'Sleeper Class' }
  ],
  bus: [
    { value: 'ac_sleeper', label: 'Sleeper' },
    { value: 'ac_seater', label: 'Seater' }
  ],
  cab: [
    { value: 'sedan', label: 'Sedan (Dzire / Etios)' },
    { value: 'suv', label: 'SUV (Innova / Ertiga)' },
    { value: 'hatchback', label: 'Hatchback (Indica / WagonR)' }
  ]
};

const EditTravelRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [requestDoc, setRequestDoc] = useState(null);
  const [reworkNotes, setReworkNotes] = useState('');
  const [employeesDirectory, setEmployeesDirectory] = useState([]);

  const [formData, setFormData] = useState({
    purpose: '',
    travel_type: 'flight',
    travel_category: 'Domestic',
    booking_responsibility: 'company_booked',
    is_emergency: false,
    is_international: false,
    department_id: '',
    department_name: '',
    department: '',
    project_id: '',
    project_code: '',
    project_name: '',
    project: '',
    cost_center: '',
    gl_account: '',
    estimated_budget: 0,
    remarks: '',
    attachments: [],

    journey_details: [
      {
        segment_number: 1,
        from_city: '',
        to_city: '',
        departure_date: '',
        return_date: '',
        preferred_time: 'Morning (06:00 - 12:00)',
        travel_mode: 'flight',
        booking_preference: 'economy',
        class_entitlement: 'Economy Class'
      }
    ],

    passengers: [
      {
        employee_id: '',
        employee_code: '',
        name: '',
        age: '',
        gender: 'Male',
        id_type: 'Aadhaar Card',
        id_number: '',
        is_guest: false,
      }
    ],

    advance_requested: false,
    advance_owner_employee_id: '',
    advance_owner_name: '',
    advance_hotel: 0,
    advance_food: 0,
    advance_local_travel: 0,
    advance_misc: 0,
  });

  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchRequest();
    axios.get(`${API_URL}/employees/minimal`, { withCredentials: true })
      .then(({ data }) => setEmployeesDirectory(Array.isArray(data) ? data : (data?.data || [])))
      .catch(() => setEmployeesDirectory([]));
  }, [id]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/requests/${id}`, { withCredentials: true });
      const req = data.request;
      setRequestDoc(req);
      setReworkNotes(req.rework_notes || '');

      const loadedLegs = (req.journey_details && req.journey_details.length > 0)
        ? req.journey_details
        : [{
            segment_number: 1,
            from_city: req.origin_city || req.from_city || '',
            to_city: req.destination_city || req.to_city || '',
            departure_date: req.start_date || req.departure_date || '',
            return_date: req.end_date || req.return_date || '',
            preferred_time: 'Morning (06:00 - 12:00)',
            travel_mode: req.travel_mode || req.travel_type || 'flight',
            booking_preference: 'economy',
            class_entitlement: 'Economy Class'
          }];

      setFormData({
        purpose: req.purpose || '',
        travel_type: req.travel_type || 'flight',
        travel_category: req.travel_category || 'Domestic',
        booking_responsibility: req.booking_responsibility || 'company_booked',
        is_emergency: !!req.is_emergency,
        is_international: !!req.is_international,
        department_id: req.department_id || '',
        department_name: req.department_name || req.department || '',
        department: req.department_name || req.department || '',
        project_id: req.project_id || '',
        project_code: req.project_code || '',
        project_name: req.project_name || req.project || '',
        project: req.project_name || req.project || '',
        cost_center: req.cost_center || '',
        gl_account: req.gl_account || '',
        estimated_budget: req.estimated_budget || 0,
        remarks: req.remarks || '',
        attachments: req.attachments || [],
        journey_details: loadedLegs,
        passengers: req.passengers || [],
        advance_requested: !!req.advance_requested,
        advance_owner_employee_id: req.advance_details?.advance_owner_employee_id || req.employee_id || '',
        advance_owner_name: req.advance_details?.advance_owner_name || req.employee_name || '',
        advance_hotel: req.advance_details?.hotel_amount || 0,
        advance_food: req.advance_details?.food_amount || 0,
        advance_local_travel: req.advance_details?.local_travel_amount || 0,
        advance_misc: req.advance_details?.misc_amount || 0,
      });

      const compId = req.company_id || '';
      axios.get(`${API_URL}/masters/departments?company_id=${compId}&active_only=true`, { withCredentials: true })
        .then(({ data: deptsData }) => setDepartments(Array.isArray(deptsData) ? deptsData : []))
        .catch(() => setDepartments([]));

      axios.get(`${API_URL}/masters/projects?company_id=${compId}&active_only=true`, { withCredentials: true })
        .then(({ data: projData }) => setProjects(Array.isArray(projData) ? projData : []))
        .catch(() => setProjects([]));
    } catch (err) {
      toast.error('Failed to load travel request');
    } finally {
      setLoading(false);
    }
  };

  const updateJourneyField = (idx, field, value) => {
    const nextLegs = [...formData.journey_details];
    nextLegs[idx][field] = value;
    if (field === 'to_city' && idx + 1 < nextLegs.length) {
      nextLegs[idx + 1].from_city = value;
    }
    setFormData({ ...formData, journey_details: nextLegs });
  };

  const addJourneyLeg = () => {
    const prevLegs = formData.journey_details;
    const lastLeg = prevLegs[prevLegs.length - 1];
    const autoMode = lastLeg ? lastLeg.travel_mode : (formData.travel_type !== 'multi_city' ? formData.travel_type : 'flight');
    const opts = BOOKING_PREFERENCE_OPTIONS[autoMode] || BOOKING_PREFERENCE_OPTIONS.flight;
    const newLeg = {
      segment_number: prevLegs.length + 1,
      from_city: lastLeg ? lastLeg.to_city : '',
      to_city: '',
      departure_date: lastLeg ? lastLeg.departure_date : '',
      return_date: '',
      preferred_time: 'Morning (06:00 - 12:00)',
      travel_mode: autoMode,
      booking_preference: opts[0]?.value || 'economy',
      class_entitlement: opts[0]?.label || 'Standard Class'
    };
    const nextLegs = [...prevLegs, newLeg];
    setFormData({
      ...formData,
      travel_type: nextLegs.length > 1 ? 'multi_city' : formData.travel_type,
      journey_details: nextLegs
    });
    toast.info(`Added Location #${nextLegs.length}`);
  };

  const removeJourneyLeg = (idx) => {
    if (formData.journey_details.length === 1) return;
    const filtered = formData.journey_details.filter((_, i) => i !== idx);
    const updated = filtered.map((leg, i) => ({ ...leg, segment_number: i + 1 }));
    setFormData({
      ...formData,
      travel_type: updated.length > 1 ? 'multi_city' : (formData.travel_type === 'multi_city' ? 'flight' : formData.travel_type),
      journey_details: updated
    });
  };

  const handleSearchPassengerCode = async (idx, codeInput) => {
    const codeToSearch = (codeInput || formData.passengers[idx]?.search_code_input || formData.passengers[idx]?.employee_code || '').trim();
    if (!codeToSearch) {
      toast.error('Please enter an Employee Code.');
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_URL}/travel-desk/validate-employee-code?code=${encodeURIComponent(codeToSearch)}`,
        { withCredentials: true }
      );

      const empIdStr = String(data.employee_id || data.id);
      const isDup = formData.passengers.some((p, i) => i !== idx && String(p.employee_id) === empIdStr);
      if (isDup) {
        toast.error('This employee has already been added as a passenger.');
        return;
      }

      const nextPax = [...formData.passengers];
      nextPax[idx] = {
        ...nextPax[idx],
        employee_id: empIdStr,
        employee_code: data.employee_code,
        name: data.employee_name,
        gender: data.gender || nextPax[idx].gender || 'Male',
        department: data.department || '',
        designation: data.designation || '',
        search_code_input: data.employee_code,
        is_validated: true,
        is_guest: false
      };

      setFormData({ ...formData, passengers: nextPax });
      toast.success(`Validated Employee: ${data.employee_name} (${data.employee_code})`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Employee not found. Please enter a valid Employee Code.';
      toast.error(msg);
    }
  };

  const handleSearchAdvanceOwnerCode = async (codeToSearch) => {
    const cleanCode = (codeToSearch || '').trim();
    if (!cleanCode) {
      toast.error('Please enter an Employee Code.');
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_URL}/travel-desk/validate-employee-code?code=${encodeURIComponent(cleanCode)}`,
        { withCredentials: true }
      );

      setFormData({
        ...formData,
        advance_owner_employee_id: String(data.employee_id || data.id),
        advance_owner_name: data.employee_name,
        advance_owner_code: data.employee_code,
        advance_owner_input: data.employee_code
      });
      toast.success(`Advance Owner assigned: ${data.employee_name} (${data.employee_code})`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Employee not found. Please enter a valid Employee Code.';
      toast.error(msg);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      setUploadingAttachment(true);
      const newAtts = [...(formData.attachments || [])];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        try {
          const { data } = await axios.post(`${API_URL}/travel-desk/upload-attachment`, fd, { withCredentials: true });
          newAtts.push(data);
        } catch (err) {
          const b64 = await new Promise((res) => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.onerror = () => res(null);
            reader.readAsDataURL(file);
          });
          newAtts.push({
            name: file.name,
            filename: file.name,
            size: file.size,
            data_url: b64,
            type: file.type
          });
        }
      }
      setFormData(prev => ({ ...prev, attachments: newAtts }));
      toast.success('Supporting attachment(s) uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRemoveAttachment = (idx) => {
    setFormData(prev => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, i) => i !== idx)
    }));
  };

  const handleSave = async (e, actionType = 'save') => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      setSaving(true);
      if (actionType === 'resubmit') {
        const payload = {
          ...formData,
          rework_notes: reworkNotes.trim() || 'Request updated and resubmitted after rework'
        };
        await axios.post(`${API_URL}/travel-desk/requests/${id}/resubmit`, payload, { withCredentials: true });
        toast.success('Travel request reworked and resubmitted successfully!');
      } else if (actionType === 'submit') {
        if (!formData.purpose.trim()) {
          toast.error('Please enter the travel purpose.');
          setSaving(false);
          return;
        }
        await axios.patch(`${API_URL}/travel-desk/requests/${id}`, {
          ...formData,
          status: 'submitted',
          submit: true
        }, { withCredentials: true });
        toast.success('Travel request submitted for approval successfully!');
      } else if (actionType === 'draft') {
        await axios.patch(`${API_URL}/travel-desk/requests/${id}`, {
          ...formData,
          status: 'draft'
        }, { withCredentials: true });
        toast.success('Travel request draft saved successfully!');
      } else {
        await axios.patch(`${API_URL}/travel-desk/requests/${id}`, formData, { withCredentials: true });
        toast.success('Travel request updated successfully!');
      }
      navigate(`/travel/request/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update travel request');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-500 font-semibold">Loading request...</div>;
  }

  const totalAdv = formData.advance_requested
    ? (Number(formData.advance_hotel) || 0) +
      (Number(formData.advance_food) || 0) +
      (Number(formData.advance_local_travel) || 0) +
      (Number(formData.advance_misc) || 0)
    : 0;

  return (
    <div className="p-6 space-y-6 max-w-[1100px] mx-auto font-sans pb-20">
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/travel/request/${id}`)} className="p-2 bg-white/10 rounded-xl hover:bg-white/20">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-black">Edit Travel Request</h1>
            <p className="text-xs text-blue-200">Modify route segments, passengers, preferences, and advances.</p>
          </div>
        </div>
      </div>

      {/* Sent Back Banner */}
      {requestDoc && (['returned_for_correction', 'sent_back'].includes(requestDoc.status) || requestDoc.approval_status === 'sent_back') && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 flex items-start gap-3 shadow-sm">
          <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="font-black text-amber-950 text-xs uppercase tracking-wide">
              Application Returned for Rework / Correction
            </h4>
            <p className="text-amber-900 text-xs font-medium">
              <b>Approver Remarks:</b> {requestDoc.return_remarks || 'Please modify the required details and resubmit.'}
            </p>
            {requestDoc.sent_back_by && (
              <div className="text-[11px] text-amber-800 font-semibold">
                Sent back by: {requestDoc.sent_back_by} {requestDoc.sent_back_at ? `on ${requestDoc.sent_back_at.slice(0, 10)}` : ''}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Draft Banner */}
      {requestDoc && requestDoc.status === 'draft' && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
          <AlertCircle className="w-5 h-5 text-blue-600 shrink-0" />
          <p className="text-blue-900 text-xs font-medium">
            You are editing a <b>Draft Application</b>. All previously saved fields and route details have been loaded. You can update details and save draft again or submit for approval.
          </p>
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <form onSubmit={(e) => handleSave(e, 'save')} className="space-y-5 text-xs">
          {/* Rework Notes input when application is returned */}
          {requestDoc && (['returned_for_correction', 'sent_back'].includes(requestDoc.status) || requestDoc.approval_status === 'sent_back') && (
            <div className="p-4 bg-amber-50/70 border border-amber-200 rounded-xl space-y-1.5">
              <label className="block font-bold text-amber-950 text-xs">
                Employee Rework Notes / Explanations of Changes Made *
              </label>
              <textarea
                rows={3}
                value={reworkNotes}
                onChange={(e) => setReworkNotes(e.target.value)}
                placeholder="Explain the changes made in response to the approver's remarks..."
                className="w-full p-2.5 bg-white border border-amber-300 rounded-xl font-medium outline-none focus:ring-2 focus:ring-amber-500/20"
                required
              />
            </div>
          )}

          <div>
            <label className="block font-bold text-slate-700 mb-1">Travel Purpose *</label>
            <input
              type="text"
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-semibold outline-none"
              required
            />
          </div>

          {/* TICKET BOOKING RESPONSIBILITY */}
          <div className="space-y-2 pt-2 border-t border-slate-100" data-testid="field-booking-responsibility">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-slate-700">
                Ticket Booking Responsibility *
              </label>
              <span className="text-[11px] text-slate-400 font-medium">
                Choose who arranges and books journey tickets
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, booking_responsibility: 'self_booked' }))}
                className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 cursor-pointer ${
                  formData.booking_responsibility === 'self_booked'
                    ? 'bg-indigo-50/80 border-indigo-500 ring-2 ring-indigo-500/20 shadow-sm'
                    : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                }`}
                data-testid="option-self-booked"
              >
                <input
                  type="radio"
                  name="booking_responsibility"
                  value="self_booked"
                  checked={formData.booking_responsibility === 'self_booked'}
                  onChange={() => setFormData(prev => ({ ...prev, booking_responsibility: 'self_booked' }))}
                  className="mt-0.5 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                <div>
                  <div className="font-bold text-xs text-slate-900 flex items-center gap-1.5">
                    <span>Self-Booked</span>
                    <span className="text-[10px] font-bold text-indigo-700 bg-indigo-100/80 px-2 py-0.5 rounded-full">
                      Employee Arranges Ticket
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                    Employee arranges own journey tickets directly. Request travel advances freely without mandatory company ticket preferences.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, booking_responsibility: 'company_booked' }))}
                className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 cursor-pointer ${
                  formData.booking_responsibility === 'company_booked'
                    ? 'bg-blue-50/80 border-blue-500 ring-2 ring-blue-500/20 shadow-sm'
                    : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                }`}
                data-testid="option-company-booked"
              >
                <input
                  type="radio"
                  name="booking_responsibility"
                  value="company_booked"
                  checked={formData.booking_responsibility === 'company_booked'}
                  onChange={() => setFormData(prev => ({ ...prev, booking_responsibility: 'company_booked' }))}
                  className="mt-0.5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <div>
                  <div className="font-bold text-xs text-slate-900 flex items-center gap-1.5">
                    <span>Company-Booked</span>
                    <span className="text-[10px] font-bold text-blue-700 bg-blue-100/80 px-2 py-0.5 rounded-full">
                      Company Arranges Ticket
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                    Travel Desk or designated corporate booking controller books tickets and itineraries in line with entitlement policy.
                  </p>
                </div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block font-bold text-slate-700 mb-1">Primary Travel Mode *</label>
              <select
                value={formData.travel_type}
                onChange={(e) => {
                  const newType = e.target.value;
                  const opts = BOOKING_PREFERENCE_OPTIONS[newType] || BOOKING_PREFERENCE_OPTIONS.flight;
                  const nextLegs = formData.journey_details.map(leg => ({
                    ...leg,
                    travel_mode: newType,
                    booking_preference: opts[0]?.value || 'economy',
                    class_entitlement: opts[0]?.label || 'Standard Class'
                  }));
                  setFormData({
                    ...formData,
                    travel_type: newType,
                    journey_details: nextLegs
                  });
                }}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-semibold"
              >
                <option value="flight">Flight</option>
                <option value="train">Train</option>
                <option value="bus">Bus</option>
                <option value="cab">Cab / Taxi</option>
                {formData.travel_type === 'multi_city' && (
                  <option value="multi_city" disabled>
                    MULTI CITY (Legacy)
                  </option>
                )}
              </select>
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Travel Category</label>
              <select
                value={formData.travel_category || 'Domestic'}
                onChange={(e) => {
                  const cat = e.target.value;
                  setFormData({
                    ...formData,
                    travel_category: cat,
                    is_international: cat === 'International'
                  });
                }}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-semibold"
              >
                <option value="Domestic">Domestic</option>
                <option value="International">International</option>
              </select>
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Department *</label>
              <select
                value={formData.department_id || ''}
                onChange={(e) => {
                  const selId = e.target.value;
                  const found = departments.find(d => String(d.id || d._id) === String(selId));
                  setFormData({
                    ...formData,
                    department_id: selId,
                    department_name: found ? found.name : formData.department_name,
                    department: found ? found.name : formData.department,
                  });
                }}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-semibold outline-none"
                required
              >
                <option value="">{formData.department_name ? `Current: ${formData.department_name}` : '-- Select Department --'}</option>
                {departments.map((d) => (
                  <option key={d.id || d._id} value={d.id || d._id}>
                    {d.name} {d.code ? `(${d.code})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Project *</label>
              <SearchableProjectSelect
                projects={projects}
                value={formData.project_id || ''}
                onChange={(found) => {
                  setFormData({
                    ...formData,
                    project_id: found ? (found.id || found._id) : '',
                    project_name: found ? (found.name || found.project_name) : '',
                    project_code: found ? (found.code || found.project_code) : '',
                    project: found ? (found.name || found.project_name) : '',
                  });
                }}
                required
              />
            </div>
          </div>

          {/* Route & Dates Subsection */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <h4 className="font-bold text-xs uppercase text-slate-700 tracking-wider flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-blue-500" /> Route &amp; Dates ({formData.journey_details.length} Segment{formData.journey_details.length > 1 ? 's' : ''})
              </h4>
              <button
                type="button"
                onClick={addJourneyLeg}
                className="px-3 py-1 bg-indigo-50 text-indigo-700 font-bold text-xs rounded-lg border border-indigo-200 flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add Location
              </button>
            </div>

            {formData.journey_details.map((leg, idx) => (
              <div key={idx} className="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">From City *</label>
                  <input
                    type="text"
                    value={leg.from_city}
                    onChange={(e) => updateJourneyField(idx, 'from_city', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">To City *</label>
                  <input
                    type="text"
                    value={leg.to_city}
                    onChange={(e) => updateJourneyField(idx, 'to_city', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Departure Date *</label>
                  <input
                    type="date"
                    value={leg.departure_date}
                    onChange={(e) => updateJourneyField(idx, 'departure_date', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Return Date</label>
                  <input
                    type="date"
                    value={leg.return_date || ''}
                    onChange={(e) => updateJourneyField(idx, 'return_date', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                  />
                </div>
                <div className="flex items-end justify-between gap-2">
                  <div className="flex-1">
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Travel Mode</label>
                    <select
                      value={leg.travel_mode || 'flight'}
                      onChange={(e) => updateJourneyField(idx, 'travel_mode', e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                    >
                      <option value="flight">Flight</option>
                      <option value="train">Train</option>
                      <option value="bus">Bus</option>
                      <option value="cab">Cab</option>
                    </select>
                  </div>
                  {formData.journey_details.length > 1 && (
                    <button type="button" onClick={() => removeJourneyLeg(idx)} className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Passenger Section */}
          <div className="space-y-3 pt-2">
            <h4 className="font-bold text-xs uppercase text-slate-700 tracking-wider flex items-center gap-1.5">
              <Users className="w-4 h-4 text-blue-500" /> Passenger Manifest ({formData.passengers.length})
            </h4>

            {formData.passengers.map((pax, idx) => (
              <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end text-xs">
                  <div className="md:col-span-2 space-y-1">
                    <label className="block text-[11px] font-bold text-slate-600">Enter Employee Code *</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="e.g. S2248"
                        value={pax.search_code_input !== undefined ? pax.search_code_input : (pax.employee_code || '')}
                        onChange={(e) => {
                          const nextPax = [...formData.passengers];
                          nextPax[idx].search_code_input = e.target.value;
                          setFormData({ ...formData, passengers: nextPax });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearchPassengerCode(idx);
                          }
                        }}
                        className="flex-1 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold outline-none focus:border-blue-500 uppercase"
                      />
                      <button
                        type="button"
                        onClick={() => handleSearchPassengerCode(idx)}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-lg flex items-center gap-1 shadow-sm shrink-0"
                      >
                        <Search className="w-3.5 h-3.5" /> Validate
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Full Name</label>
                    <input
                      type="text"
                      placeholder="Passenger Name"
                      value={pax.name || ''}
                      onChange={(e) => {
                        const nextPax = [...formData.passengers];
                        nextPax[idx].name = e.target.value;
                        setFormData({ ...formData, passengers: nextPax });
                      }}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Gender</label>
                    <select
                      value={pax.gender || 'Male'}
                      onChange={(e) => {
                        const nextPax = [...formData.passengers];
                        nextPax[idx].gender = e.target.value;
                        setFormData({ ...formData, passengers: nextPax });
                      }}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {pax.employee_id && (
                  <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-200 text-[11px] text-emerald-900 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      <span><b>Validated Passenger:</b> {pax.name} ({pax.employee_code})</span>
                    </div>
                    <span className="font-mono text-[10px] text-slate-500">ID: {pax.employee_id}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Advance Request Block */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
            <label className="flex items-center gap-2 font-bold text-slate-800 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.advance_requested}
                onChange={(e) => setFormData({ ...formData, advance_requested: e.target.checked })}
                className="w-4 h-4 rounded text-blue-600"
              />
              Request Travel Advance
            </label>

            {formData.advance_requested && (
              <div className="space-y-3 pt-1">
                <div className="space-y-1 text-xs">
                  <label className="block font-bold text-slate-700">Advance Assigned Owner</label>
                  <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                    <div className="flex-1 w-full">
                      <select
                        value={formData.advance_owner_employee_id || ''}
                        onChange={(e) => {
                          const selEmpId = e.target.value;
                          const foundPax = formData.passengers.find(p => String(p.employee_id) === String(selEmpId));
                          if (foundPax && foundPax.employee_id) {
                            setFormData({
                              ...formData,
                              advance_owner_employee_id: foundPax.employee_id,
                              advance_owner_name: foundPax.name,
                              advance_owner_code: foundPax.employee_code,
                              advance_owner_input: foundPax.employee_code
                            });
                          }
                        }}
                        className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                      >
                        <option value={formData.advance_owner_employee_id || ''}>
                          {formData.advance_owner_name || 'Select Passenger Owner'} ({formData.advance_owner_code || 'EMP'})
                        </option>
                        {formData.passengers.filter(p => p.employee_id).map((pax, i) => (
                          <option key={i} value={pax.employee_id}>
                            {pax.name} ({pax.employee_code || 'EMP'})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="text-xs font-bold text-slate-400 self-center uppercase">OR</div>

                    <div className="flex-1 w-full flex gap-2">
                      <input
                        type="text"
                        placeholder="Search Code e.g. S2248"
                        value={formData.advance_owner_input !== undefined ? formData.advance_owner_input : (formData.advance_owner_code || '')}
                        onChange={(e) => setFormData({ ...formData, advance_owner_input: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearchAdvanceOwnerCode(formData.advance_owner_input);
                          }
                        }}
                        className="flex-1 px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold uppercase"
                      />
                      <button
                        type="button"
                        onClick={() => handleSearchAdvanceOwnerCode(formData.advance_owner_input)}
                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs rounded-lg flex items-center gap-1 shadow-sm shrink-0"
                      >
                        <Search className="w-3.5 h-3.5" /> Validate
                      </button>
                    </div>
                  </div>

                  {formData.advance_owner_name && (
                    <div className="p-2 bg-amber-50 rounded-lg border border-amber-200 text-[11px] text-amber-950 flex items-center justify-between flex-wrap gap-2 font-medium mt-1">
                      <div className="flex items-center gap-1.5">
                        <Wallet className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <span><b>Assigned Advance Owner:</b> {formData.advance_owner_name} ({formData.advance_owner_code || 'EMP'})</span>
                      </div>
                      <span className="font-mono text-[10px] text-slate-500">ID: {formData.advance_owner_employee_id}</span>
                    </div>
                  )}
                </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Hotel (₹)</label>
                  <input
                    type="number"
                    value={formData.advance_hotel}
                    onChange={(e) => setFormData({ ...formData, advance_hotel: Number(e.target.value) })}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Food (₹)</label>
                  <input
                    type="number"
                    value={formData.advance_food}
                    onChange={(e) => setFormData({ ...formData, advance_food: Number(e.target.value) })}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Local Travel (₹)</label>
                  <input
                    type="number"
                    value={formData.advance_local_travel}
                    onChange={(e) => setFormData({ ...formData, advance_local_travel: Number(e.target.value) })}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Misc (₹)</label>
                  <input
                    type="number"
                    value={formData.advance_misc}
                    onChange={(e) => setFormData({ ...formData, advance_misc: Number(e.target.value) })}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg font-mono font-bold"
                  />
                </div>
                <div className="sm:col-span-2 md:col-span-4 p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-amber-900 font-bold flex justify-between items-center">
                  <span>Total Advance Requested:</span>
                  <span className="font-mono text-sm">₹ {totalAdv.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          )}
          </div>

          {/* SUPPORTING ATTACHMENTS */}
          <div className="space-y-3 pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <label className="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <Paperclip className="w-3.5 h-3.5 text-blue-600" /> Supporting Attachments
                </label>
                <span className="text-[11px] text-slate-400">
                  Attach quotes, invites, approvals, or travel documents (PDF, Images, Excel)
                </span>
              </div>
              <label className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 font-bold text-xs rounded-xl cursor-pointer flex items-center gap-1 transition-colors">
                <UploadCloud className="w-3.5 h-3.5" />
                {uploadingAttachment ? 'Uploading...' : 'Upload Files'}
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={uploadingAttachment}
                  className="hidden"
                />
              </label>
            </div>

            {formData.attachments && formData.attachments.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {formData.attachments.map((att, idx) => {
                  const attName = att.name || att.filename || `Attachment #${idx + 1}`;
                  return (
                    <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-xl gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="text-xs font-semibold text-slate-800 truncate" title={attName}>
                          {attName}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(idx)}
                        className="p-1 text-rose-500 hover:bg-rose-50 rounded cursor-pointer"
                        title="Remove file"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-400 text-xs italic">No supporting attachments uploaded.</p>
            )}
          </div>

          <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => navigate(`/travel/request/${id}`)}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-slate-700 rounded-xl cursor-pointer"
            >
              Cancel
            </button>

            {requestDoc?.status === 'draft' ? (
              <>
                <button
                  type="button"
                  onClick={(e) => handleSave(e, 'draft')}
                  disabled={saving}
                  className="px-5 py-2 bg-slate-200 hover:bg-slate-300 text-slate-900 font-bold rounded-xl flex items-center gap-1.5 cursor-pointer"
                >
                  <Save className="w-4 h-4 text-slate-600" /> Save as Draft
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSave(e, 'submit')}
                  disabled={saving}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold rounded-xl shadow-md flex items-center gap-1.5 cursor-pointer"
                >
                  <Send className="w-4 h-4" /> {saving ? 'Submitting...' : 'Submit Travel Request'}
                </button>
              </>
            ) : requestDoc && (['returned_for_correction', 'sent_back'].includes(requestDoc.status) || requestDoc.approval_status === 'sent_back') ? (
              <>
                <button
                  type="button"
                  onClick={(e) => handleSave(e, 'save')}
                  disabled={saving}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-900 font-bold rounded-xl flex items-center gap-1.5 cursor-pointer"
                >
                  <Save className="w-4 h-4 text-slate-600" /> Save Changes
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSave(e, 'resubmit')}
                  disabled={saving}
                  className="px-6 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black rounded-xl shadow-md flex items-center gap-1.5 cursor-pointer"
                >
                  <RotateCcw className="w-4 h-4" /> {saving ? 'Resubmitting...' : 'Resubmit Reworked Request'}
                </button>
              </>
            ) : (
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white font-extrabold rounded-xl shadow-md flex items-center gap-1.5 cursor-pointer"
              >
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTravelRequest;
